/**
 * An example value for each filter, in a documented query parameter.
 *
 * Derived from the control the filter is edited with rather than transcribed
 * from upstream, where `getExample` is spread across a five-deep class
 * hierarchy: `higher_than` gets `'100'` from `NumericComparisonViewFilterType`,
 * the multi-step date filters get `'UTC??today'` from their base, and
 * thirty-three of the ninety-one override it themselves. Reading it off the
 * input kind gives the same answers from a map the compiler can check is
 * complete -- and the kinds are already the thing that decides what a value
 * looks like.
 */
import type { Field, ViewFilterTypeName } from '@/api/types'
import { filterInputKind, type FilterInputKind } from '../filters/inputs'

const EXAMPLES = {
  none: '',
  text: 'string',
  number: '100',
  duration: '1:23',
  rating: '3',
  boolean: 'true',
  'select-one': '1',
  'select-many': '1,2',
  user: '1',
  'link-row': '1',
  'file-type': 'image',
  date: 'UTC??today',
  'day-of-month': '1',
  'legacy-date': '2020-01-01',
} as const satisfies Record<FilterInputKind, string>

export function filterExample(type: ViewFilterTypeName, field: Field): string {
  return EXAMPLES[filterInputKind(type, field)]
}

/**
 * The whole query parameter, as it would be written.
 *
 * The field is named the way the reader is currently reading the examples,
 * because `filter__Name__equal` and `filter__field_11__equal` are both correct
 * and only one of them matches the request above it on the page.
 */
export function filterParameter(
  type: ViewFilterTypeName,
  field: Field,
  fieldKey: string,
): string {
  const value = filterExample(type, field)
  return `filter__${fieldKey}__${type}=${value}`
}
